import { AUDIO, type CanalAudio } from './settings';
import { arquivoDaMagia, magiasDeDano } from './skillSounds';
import {
  AMBIENTE, AVISO_UI, COLETA, DOR_F, DOR_M, ITEM, MAGIA_UO, MORTE_F, MORTE_M, MUNDO, OFICIO, EVENTO, sorteia, urlUo
} from './sonsUo';

/**
 * O mixer do jogo (docs/proposta-sons.md §07). Os sons da UO tocam por `tocarUo(id, canal)`; as magias antigas
 * continuam em /sounds/skill/. Regras contra a enxurrada da caçada idle: no máximo VOZES_MAX ao mesmo tempo
 * (o que é seu passa na frente) e uma `chave` com intervalo mínimo entre dois toques iguais.
 */
type Ctx = AudioContext;
type Canal = CanalAudio | 'master';

const VOZES_MAX = 10;
const buffers = new Map<string, AudioBuffer>();
const loading = new Map<string, Promise<AudioBuffer | null>>();
const ultimoToque = new Map<string, number>();
let ctx: Ctx | null = null;
let vozes = 0;

function audioCtx(): Ctx {
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
  }
  return ctx;
}

function unlock() {
  const c = audioCtx();
  if (c.state === 'suspended') void c.resume();
}

/**
 * Teto do som (techspec-hud-v3 §24): o jogo saía alto demais na primeira vez. Tudo toca a 70% do que tocava;
 * o Geral em 100% equivale a 70% de antes.
 */
const TETO = 0.7;

function gain(canal: Canal): number {
  if (AUDIO.muted) return 0;
  const cat = canal === 'master' ? 1 : AUDIO[canal];
  return Math.min(1, Math.max(0, AUDIO.master * cat * TETO));
}

function fire(buf: AudioBuffer, vol: number) {
  const c = audioCtx();
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  g.gain.value = vol;
  src.connect(g);
  g.connect(c.destination);
  vozes += 1;
  src.onended = () => { vozes = Math.max(0, vozes - 1); };
  src.start(0);
}

function loadUrl(url: string): Promise<AudioBuffer | null> {
  const ready = buffers.get(url);
  if (ready) return Promise.resolve(ready);
  const inflight = loading.get(url);
  if (inflight) return inflight;
  const job = fetch(url)
    .then(res => {
      if (!res.ok) throw new Error(String(res.status));
      return res.arrayBuffer();
    })
    .then(raw => audioCtx().decodeAudioData(raw))
    .then(buf => {
      buffers.set(url, buf);
      loading.delete(url);
      return buf;
    })
    .catch(() => {
      loading.delete(url);
      return null;
    });
  loading.set(url, job);
  return job;
}

function tocarUrl(url: string, canal: Canal, mult = 1, prioridade = false): void {
  const vol = gain(canal) * Math.min(1, Math.max(0, mult));
  if (vol <= 0.01 || !url) return;
  if (!prioridade && vozes >= VOZES_MAX) return;
  unlock();
  const buf = buffers.get(url);
  if (buf) {
    fire(buf, vol);
    return;
  }
  void loadUrl(url).then(decoded => {
    if (!decoded) return;
    const late = gain(canal) * Math.min(1, Math.max(0, mult));
    if (late > 0.01) fire(decoded, late);
  });
}

export interface OpcoesSom {
  /** 0..1, multiplica o volume do canal (a distância, por exemplo). */
  readonly vol?: number;
  /** Dois toques com a mesma chave respeitam `intervaloMs`. */
  readonly chave?: string;
  readonly intervaloMs?: number;
  /** O que é seu: passa mesmo com as vozes cheias. */
  readonly prioridade?: boolean;
}

/** Toca um som da UO pelo id do soundLegacyMUL. */
export function tocarUo(id: number, canal: Canal, o: OpcoesSom = {}): void {
  if (id < 0) return;
  if (o.chave) {
    const agora = performance.now();
    const antes = ultimoToque.get(o.chave) ?? -Infinity;
    if (agora - antes < (o.intervaloMs ?? 300)) return;
    ultimoToque.set(o.chave, agora);
    if (ultimoToque.size > 500) ultimoToque.clear();
  }
  tocarUrl(urlUo(id), canal, o.vol ?? 1, o.prioridade);
}

/** Um dos ids, sorteado, sem repetir o último da mesma lista. */
const ultimoDaLista = new WeakMap<readonly number[], number>();
export function tocarUm(ids: readonly number[], canal: Canal, o: OpcoesSom = {}): void {
  const id = sorteia(ids, ultimoDaLista.get(ids) ?? -1);
  ultimoDaLista.set(ids, id);
  tocarUo(id, canal, o);
}

export function prefetchUo(ids: Iterable<number>): void {
  for (const id of ids) if (id >= 0) void loadUrl(urlUo(id));
}

/* ------------------------------------------------------------------ magias e o antigo */

/** Toca no mesmo turno do `a`. Buffer já decodificado = start(0) síncrono. */
export function playSkill(spellId: string): void {
  const uo = MAGIA_UO[spellId];
  if (uo !== undefined) { tocarUo(uo, 'skills', { prioridade: true }); return; }
  const file = arquivoDaMagia(spellId);
  if (file) tocarUrl(`/sounds/skill/${file}`, 'skills', 1, true);
}

export function playAviso(): void {
  tocarUo(AVISO_UI, 'master', { chave: 'aviso', intervaloMs: 600, prioridade: true });
}

/** A falha de magia: 0x5C é o id oficial do fizzle (o arquivo se chama aligatr3). */
export function playFizzle(): void {
  tocarUo(0x5C, 'skills', { prioridade: true });
}

/** A sua dor, pelo gênero. Sem gênero (a tela de opções), a masculina. */
export function playHurt(feminino = false): void {
  tocarUm(feminino ? DOR_F : DOR_M, 'combate', { chave: 'dor:voce', intervaloMs: 250, prioridade: true });
}

export function playDie(feminino = false): void {
  tocarUm(feminino ? MORTE_F : MORTE_M, 'combate', { prioridade: true });
}

export function playForja(): void { tocarUo(OFICIO.forja, 'skills'); }
export function playAlquimia(): void { tocarUo(OFICIO.alquimia, 'skills'); }
export function playAlfaiataria(): void { tocarUo(OFICIO.alfaiataria, 'skills'); }
export function playArcos(): void { tocarUo(OFICIO.arcos, 'skills'); }
export function playGarrafa(): void { tocarUo(OFICIO.garrafa, 'skills'); }

/* ------------------------------------------------------------------ itens e mundo */

/** O golpe da ferramenta na coleta: chamado no quadro em que a picareta ou o machado bate (engine.ts). */
export function playColeta(minerio: boolean, vol = 1, prioridade = true): void {
  tocarUm(minerio ? COLETA.minerar : COLETA.madeira, 'combate', { vol, prioridade });
}

/** Consumível usado: bandagem ou poção. */
export function playConsumivel(defId: string): void {
  tocarUo(defId === 'bandage' ? ITEM.bandagem : ITEM.pocao, 'master', { chave: 'consumo', intervaloMs: 300, prioridade: true });
}

/** Pegar do cadáver: prata pela quantidade, gema e tinta pelo brilho; o resto não toca. */
export function playPegar(defId: string, qty = 1): void {
  const i = qty <= 1 ? 0 : qty <= 5 ? 1 : 2;
  if (defId === 'silver' || defId === 'gold') tocarUo(ITEM.prata[i], 'master', { chave: 'pegar', intervaloMs: 120, prioridade: true });
  else if (/gem|tinta|ingot/.test(defId)) tocarUo(ITEM.gema[i], 'master', { chave: 'pegar', intervaloMs: 120, prioridade: true });
}

export function playComercio(): void { tocarUo(ITEM.comercio, 'master', { prioridade: true }); }
export function playBanco(): void { tocarUo(ITEM.bancoAbre, 'master', { prioridade: true }); }
export function playPortal(): void { tocarUo(MUNDO.portal, 'master', { prioridade: true }); }
export function playAnkh(): void { tocarUo(MUNDO.ankh, 'master', { prioridade: true }); }

/** Avisos do evento pelo texto (a chave do i18n). */
export function playEvento(chave: string): void {
  const id = chave === 'evento.aberto' ? EVENTO.abriu
    : chave === 'evento.agenda' ? EVENTO.aviso
      : chave === 'evento.podioAviso' ? EVENTO.podio
        : chave === 'evento.sorteio' ? ITEM.gema[1]
          : -1;
  if (id >= 0) tocarUo(id, 'master', { chave: `evento:${chave}`, intervaloMs: 2000, prioridade: true });
}

/* ------------------------------------------------------------------ ambiente */

let ambienteTimer: number | null = null;
let ambienteMapa = '';

/** Liga o ambiente do mapa (um som sorteado de tempos em tempos). Trocar de mapa troca o ambiente. */
export function iniciarAmbiente(mapaId: string): void {
  if (mapaId === ambienteMapa && ambienteTimer !== null) return;
  pararAmbiente();
  ambienteMapa = mapaId;
  const a = AMBIENTE[mapaId];
  if (!a) return;
  const proximo = () => {
    const ms = (a.minS + Math.random() * (a.maxS - a.minS)) * 1000;
    ambienteTimer = window.setTimeout(() => {
      if (document.visibilityState === 'visible') tocarUm(a.sons, 'ambiente', { vol: a.vol });
      proximo();
    }, ms);
  };
  proximo();
}

export function pararAmbiente(): void {
  if (ambienteTimer !== null) window.clearTimeout(ambienteTimer);
  ambienteTimer = null;
  ambienteMapa = '';
}

export function prefetchSkills(): void {
  unlock();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
  for (const id of magiasDeDano()) {
    const uo = MAGIA_UO[id];
    if (uo !== undefined) { prefetchUo([uo]); continue; }
    const file = arquivoDaMagia(id);
    if (file) void loadUrl(`/sounds/skill/${file}`);
  }
  const recall = arquivoDaMagia('recall');
  if (recall) void loadUrl(`/sounds/skill/${recall}`);
  prefetchUo([...DOR_F, ...DOR_M, ...MORTE_F, ...MORTE_M, 0x5C]);
}
